import { useCallback, useState } from "react"
import { LexicalText, Task } from "@/schema"
import CircleCheckbox from "@ui/circle-checkbox"
import { cn } from "@ui/utils"
import { useHotkeys } from "react-hotkeys-hook"
import { toast } from "sonner"

import { useDebounce } from "@/hooks/use-debounce"
import TaskEditor, { TaskLexicalFieldType } from "@/components/tasks/task-editor"

interface TaskHeaderProps {
  task: Task
  isCompleted: boolean
  onComplete: (checked: boolean) => void
  onEnterPressNoMention?: () => void
  onUpdateTask?: (payload: Partial<Task> & { id: string }, options?: { onSuccess?: () => void }) => void | Promise<void>
  readOnly?: boolean
}

export default function TaskHeader({
  task,
  isCompleted,
  onComplete,
  onEnterPressNoMention,
  onUpdateTask,
  readOnly = false,
}: TaskHeaderProps) {
  const [, setCurrentTitle] = useState<LexicalText | undefined>(task.title)

  useHotkeys(
    "mod+enter",
    () => {
      if (readOnly) return

      onComplete(!isCompleted)
    },
    [isCompleted, onComplete, readOnly]
  )

  const saveTitle = useCallback(
    (title?: LexicalText) => {
      if (!title || !task.id) return

      const updatePayload = {
        id: task.id,
        title,
      } as Partial<Task> & { id: string }

      onUpdateTask?.(updatePayload, {
        onSuccess: () => {
          toast.success("Task title updated.")
        },
      })
    },
    [task.id, onUpdateTask]
  )

  const debouncedUpdateTask = useDebounce((title: LexicalText) => {
    saveTitle(title)
  }, 1000)

  const handleChange = useCallback(
    (lexicalText: LexicalText, fieldType: TaskLexicalFieldType) => {
      if (fieldType === "title") {
        setCurrentTitle(lexicalText)
        debouncedUpdateTask(lexicalText)
      }
    },
    [debouncedUpdateTask]
  )

  return (
    <div className="flex items-start gap-2">
      <CircleCheckbox
        checked={isCompleted}
        onChange={onComplete}
        className="mt-1.5 flex-none"
        size="lg"
        disabled={readOnly}
      />
      <div className="grow">
        <div className={cn("relative rounded-lg", isCompleted ? "text-muted-foreground" : "")}>
          <div className="group focus-within:border-border hover:border-border relative w-full rounded-lg border border-transparent px-1">
            <TaskEditor
              onChange={handleChange}
              initialTitleContent={task.title}
              show={{ title: true, description: false }}
              styleOverrides={{
                "--lexical-editor-font-size": "18px",
                "--lexical-editor-min-height": "30px",
              }}
              onEnterPressNoMention={onEnterPressNoMention}
              hasExistingRelationship={!!task.taskable}
              editable={!readOnly}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
